// Public "Verify Certificate" entry page — user enters a verification code
// and is routed to /verify-certificate/:code (PublicVerifyPage handles the
// actual lookup + status card).
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Search, QrCode, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function VerifyCertificateEntryPage() {
  usePageTitle("Verify a Certificate");
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    // Preserve case for cert numbers with slashes; only trim whitespace.
    const c = code.trim();
    if (!c) return setErr("Please enter a verification code or certificate number.");
    if (c.length < 4) return setErr("Value is too short.");
    navigate(`/verify-certificate/${encodeURIComponent(c)}`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="verify-entry-page">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 md:h-20 md:px-6">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#101876]">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-16">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12">
          <div className="flex flex-col items-center text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101876]/10 text-[#101876]">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <div className="mt-4 text-xs font-bold uppercase tracking-[0.3em] text-[#329112]">
              Certificate Verification
            </div>
            <h1 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl" data-testid="verify-entry-title">
              Verify a Steward certificate.
            </h1>
            <p className="mt-3 max-w-lg text-sm text-slate-600">
              Enter the <b>verification code</b> (e.g. <span className="font-mono">EBE4E9CF2C68493C</span>) or
              the <b>certificate number</b> (e.g. <span className="font-mono">SIHMT/IPC-INFECT-03/2026/000001</span>)
              printed on the certificate, or scan the QR code with your phone. We&apos;ll show you the issuing
              organization, learner name, course and current status.
            </p>
          </div>

          <form onSubmit={submit} className="mx-auto mt-8 max-w-xl space-y-3" data-testid="verify-entry-form">
            <label className="block">
              <span className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">
                Verification code or Certificate number
              </span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={code}
                  onChange={(e) => { setCode(e.target.value); setErr(""); }}
                  placeholder="EBE4E9CF2C68493C or SIHMT/IPC-INFECT-03/2026/000001"
                  autoFocus
                  spellCheck={false}
                  className="w-full rounded-xl border border-slate-300 bg-white px-10 py-3.5 text-sm font-mono tracking-wider focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20"
                  data-testid="verify-entry-input"
                />
              </div>
              {err && <div className="mt-2 text-xs font-semibold text-rose-600" data-testid="verify-entry-error">{err}</div>}
            </label>

            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#101876] px-5 py-3.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-[#0d1360]"
              data-testid="verify-entry-submit"
            >
              Verify certificate <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <div className="mx-auto mt-8 grid max-w-xl gap-4 sm:grid-cols-2">
            <InfoCard
              icon={QrCode}
              title="Scan the QR"
              body="Every Steward certificate carries a unique QR — point your phone camera at it to open this page automatically."
            />
            <InfoCard
              icon={ShieldCheck}
              title="Tamper-proof"
              body="Certificates are signed and QR-verifiable. Revoked, expired or reissued statuses are shown transparently."
            />
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-6 text-center text-xs text-slate-500 md:px-6">
          © {new Date().getFullYear()} Steward Healthcare India Pvt. Ltd. · All rights reserved.
        </div>
      </footer>
    </div>
  );
}

function InfoCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[#101876]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 text-sm font-bold text-slate-900">{title}</div>
      <div className="mt-1 text-xs leading-relaxed text-slate-600">{body}</div>
    </div>
  );
}
